import OauthButtons, { oauthErrorMessage } from "@app/components/oauth-buttons";
import { setAuthToken } from "@app/relay/fetch-relay";
import { environment } from "@app/relay/relay-environment";
import { useState } from "react";
import { commitMutation, graphql } from "react-relay";

import { Field, FunnelScreen, PrimaryButton } from "../screen";

import type { emailStep_LeadConvertMutation } from "@app/queries/emailStep_LeadConvertMutation.graphql";
import { STEP, nextStep } from "../funnel";

import type { Field as FunnelField, Step } from "../funnel";
import type { EmailStepProps } from "../types";

const leadConvertMutation = graphql`
  mutation emailStep_LeadConvertMutation(
    $email: String!
    $token: String
    $nextStepKey: String
  ) {
    leadConvert(email: $email, token: $token, nextStepKey: $nextStepKey) {
      status
      token
      errors {
        message
      }
    }
  }
`;

// Why a non-business address can't continue. The funnel is a waitlist for a
// concierge onboarding of a company's books, so the address has to belong to
// the company. The list of consumer and disposable domains lives on the server
// (DisposableEmailDomain — ~170 popular ones plus tens of thousands of
// throwaways), which is why this check costs a round trip rather than shipping
// a copy of it that would drift.
const REJECTION: Record<string, string> = {
  POPULAR:
    "Please use your work email — we onboard companies, not personal accounts.",
  DISPOSABLE:
    "That looks like a disposable address. Please use your work email.",
  INVALID: "That doesn't look like a valid email address.",
  // Typing an address proves nothing, so an existing account is only reopened
  // by a provider that has checked it (Signup::Start).
  ACCOUNT_EXISTS:
    "There's already an account for that address. Continue with Google or Microsoft to sign in.",
};

// Enough to catch a typo before spending a round trip on it. The server
// classifies the address for real.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// The account card, and the one question that mints a session. On success the
// JWT is stored and the funnel reloads the same row, now with a user attached —
// the lead never leaves the page, because there is no mailer to send them to.
//
// Google and Microsoft end in the same place by a longer road: the browser goes
// to the provider and back, the server converts the lead exactly as it would for
// the address typed here, and the page collects the session from this browser's
// cookie when it boots (OauthController#handoff). A provider address that is
// refused comes back as `oauthError` and is said under the buttons, in the same
// words a typed one would get.
//
// The address is a COLUMN, not an answer: it is how we reach this person, which
// outlives the funnel, and nothing scores it.
export default function EmailStep({
  step,
  answer,
  busy,
  token,
  onConverted,
  back,
  oauthError,
}: EmailStepProps) {
  const fields = step.fields ?? [];
  // The field that mints the session. Anything else the step declares sits above
  // it, two to a row.
  const emailField = emailFieldOf(step);
  const nameFields = fields.filter((f) => f.key !== emailField?.key);

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields.map((f) => [
        f.key,
        answer?.fields.find((a) => a.key === f.key)?.value ?? "",
      ])
    )
  );
  // Nothing is wrong until they say they're done. Set on the first Continue and
  // never unset: from then on an error appears the moment it applies and goes
  // the moment it stops, which is what makes a corrected field feel corrected.
  const [submitted, setSubmitted] = useState(false);
  const [rejection, setRejection] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [providerError, setProviderError] = useState(() =>
    oauthError ? REJECTION[oauthError] ?? oauthErrorMessage(oauthError) : null
  );
  const next = nextStep(STEP.EMAIL, [], []);

  const value = (key: string) => (values[key] ?? "").trim();

  const errors: Record<string, string> = {};
  for (const field of fields) {
    if (!value(field.key)) errors[field.key] = blankMessage(field, emailField);
  }
  if (emailField && !errors[emailField.key]) {
    if (!EMAIL_SHAPE.test(value(emailField.key)))
      errors[emailField.key] = REJECTION.INVALID;
    else if (rejection) errors[emailField.key] = rejection;
  }

  const shown = (key: string) => (submitted ? errors[key] : undefined);

  const change = (key: string, next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
    // The domain verdict came from the server and can't be re-run here, so an
    // edited address simply stops carrying it.
    if (key === emailField?.key) setRejection(null);
  };

  const submit = () => {
    setSubmitted(true);
    setProviderError(null);
    if (!emailField || submitting || busy || Object.keys(errors).length > 0)
      return;
    setSubmitting(true);

    commitMutation<emailStep_LeadConvertMutation>(environment, {
      mutation: leadConvertMutation,
      variables: {
        email: value(emailField.key),
        token,
        nextStepKey: next,
      },
      onCompleted: async (response) => {
        const payload = response.leadConvert;
        if (!payload?.token) {
          setSubmitting(false);
          setRejection(
            REJECTION[payload?.status ?? "INVALID"] ?? REJECTION.INVALID
          );
          return;
        }
        setAuthToken(payload.token);
        // The row is a user's now, so the browser's handle is spent: the engine
        // drops it and reads the row back off the session instead.
        await onConverted();
        setSubmitting(false);
      },
      onError: () => {
        setSubmitting(false);
        setRejection("Something went wrong. Please try again.");
      },
    });
  };

  return (
    <FunnelScreen
      back={back}
      title={step.title}
      below={
        <p className="mt-4 px-2 text-center text-caption font-normal leading-5 text-zinc-600">
          By creating an account you agree to the{" "}
          <a href="/eula" className="underline hover:text-zinc-900">
            terms of service
          </a>{" "}
          and{" "}
          <a href="/privacy" className="underline hover:text-zinc-900">
            privacy policy
          </a>
          .
        </p>
      }
    >
      <form
        className="mt-6"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {nameFields.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {nameFields.map((field) => (
              <Field
                key={field.key}
                field={field}
                value={values[field.key] ?? ""}
                error={shown(field.key)}
                onChange={(next) => change(field.key, next)}
              />
            ))}
          </div>
        )}

        {emailField && (
          <div className="mt-4">
            <Field
              field={emailField}
              value={values[emailField.key] ?? ""}
              error={shown(emailField.key)}
              onChange={(next) => change(emailField.key, next)}
            />
          </div>
        )}

        <div className="mt-5">
          <PrimaryButton type="submit" disabled={submitting || busy}>
            Continue
          </PrimaryButton>
        </div>
      </form>

      <div className="my-6 flex items-center gap-3">
        <span aria-hidden className="h-px flex-1 bg-zinc-200" />
        <span className="text-caption uppercase tracking-[0.12em] text-zinc-400">
          or
        </span>
        <span aria-hidden className="h-px flex-1 bg-zinc-200" />
      </div>

      <OauthButtons
        intent="signup"
        leadToken={token}
        nextStepKey={next}
        disabled={submitting || busy}
      />
      {providerError && (
        <p
          role="alert"
          className="mt-3 text-center text-caption font-normal text-red-600"
        >
          {providerError}
        </p>
      )}

      <p className="mt-6 text-center text-body text-zinc-500">
        Already have an account?{" "}
        <a
          href="/login"
          className="font-medium text-blue-600 hover:text-blue-700"
        >
          Sign in
        </a>
      </p>
    </FunnelScreen>
  );
}

// The address the session is minted from: the field declared as an email, or
// the last one, so a step that forgets `inputType` still has a sign-up.
function emailFieldOf(step: Step) {
  const fields = step.fields ?? [];
  return (
    fields.find((f) => f.inputType === "email") ?? fields[fields.length - 1]
  );
}

function blankMessage(field: FunnelField, emailField: FunnelField | undefined) {
  return field.key === emailField?.key
    ? "Email cannot be blank"
    : `${field.label} cannot be blank`;
}
